const GITHUB_USER = 'cadelew';
const CONTRIBUTIONS_URL = `https://github-contributions-api.jogruber.de/v4/${GITHUB_USER}?y=last`;
const PROFILE_URL = `https://api.github.com/users/${GITHUB_USER}`;
const REPOS_URL = `https://api.github.com/users/${GITHUB_USER}/repos?per_page=100&type=owner&sort=updated`;
const CACHE_KEY = 'github-stats-cache-v1';
const CACHE_TTL_MS = 60 * 60 * 1000;
const MAX_LANGUAGE_REPOS = 12;

const LANG_COLORS = {
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  Python: '#3572a5',
  'C++': '#f34b7d',
  Rust: '#dea584',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Shell: '#89e051',
  C: '#555555',
  Java: '#b07219',
  Go: '#00add8',
};

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(value);
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: 'application/vnd.github+json',
      ...options.headers,
    },
  });
  if (!response.ok) throw new Error(`Request failed (${response.status}): ${url}`);
  return response.json();
}

function buildHeatmap(container, contributions) {
  const weeks = [];
  let currentWeek = [];

  contributions.forEach((day, index) => {
    const weekday = new Date(`${day.date}T12:00:00`).getDay();
    if (index === 0) {
      for (let i = 0; i < weekday; i += 1) currentWeek.push(null);
    }
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });
  if (currentWeek.length) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  const fragment = document.createDocumentFragment();
  weeks.forEach((week) => {
    const column = document.createElement('div');
    column.className = 'gh-week';
    week.forEach((day) => {
      const cell = document.createElement('span');
      cell.className = 'gh-day';
      if (!day) {
        cell.classList.add('is-empty');
      } else {
        cell.dataset.level = String(day.level);
        cell.title = `${day.count} contribution${day.count === 1 ? '' : 's'} on ${day.date}`;
      }
      column.append(cell);
    });
    fragment.append(column);
  });

  container.replaceChildren(fragment);
}

function buildLanguageBars(container, totals) {
  const entries = Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const sum = entries.reduce((acc, [, bytes]) => acc + bytes, 0) || 1;

  const fragment = document.createDocumentFragment();
  entries.forEach(([name, bytes]) => {
    const row = document.createElement('div');
    row.className = 'gh-lang-row';

    const label = document.createElement('span');
    label.className = 'gh-lang-name';
    label.textContent = name;

    const track = document.createElement('div');
    track.className = 'gh-lang-track';
    const fill = document.createElement('span');
    fill.className = 'gh-lang-fill';
    fill.style.width = `${(bytes / sum) * 100}%`;
    fill.style.background = LANG_COLORS[name] ?? 'var(--green)';
    track.append(fill);

    const pct = document.createElement('span');
    pct.className = 'gh-lang-pct';
    pct.textContent = `${Math.round((bytes / sum) * 100)}%`;

    row.append(label, track, pct);
    fragment.append(row);
  });

  container.replaceChildren(fragment);
}

async function loadLanguageTotals(repos) {
  const reposOwned = repos
    .filter((repo) => !repo.fork)
    .slice(0, MAX_LANGUAGE_REPOS);
  const totals = {};

  for (const repo of reposOwned) {
    try {
      const languages = await fetchJson(repo.languages_url);
      Object.entries(languages).forEach(([name, bytes]) => {
        totals[name] = (totals[name] ?? 0) + bytes;
      });
    } catch {
      // Skip individual repo language failures.
    }
  }

  return totals;
}

function applyStats(root, data, elements) {
  const {
    contribEl,
    reposEl,
    languagesEl,
    heatmapEl,
    langBarsEl,
  } = elements;

  const contributions = data.contributions ?? [];
  const yearTotal = data.yearTotal ?? 0;
  const repoCount = data.repoCount ?? null;
  const languageTotals = data.languageTotals ?? {};
  const languageCount = Object.keys(languageTotals).length;

  if (contribEl) contribEl.textContent = formatNumber(yearTotal);
  if (reposEl && repoCount !== null) reposEl.textContent = formatNumber(repoCount);
  if (languagesEl && languageCount > 0) {
    languagesEl.textContent = formatNumber(languageCount);
  }
  if (heatmapEl && contributions.length > 0) {
    buildHeatmap(heatmapEl, contributions);
  }
  if (langBarsEl && languageCount > 0) {
    buildLanguageBars(langBarsEl, languageTotals);
  }

  const hasContributions = contributions.length > 0;
  const hasRepoStats = repoCount !== null;
  const hasLanguages = languageCount > 0;

  if (!hasContributions && !hasRepoStats && !hasLanguages) {
    root.classList.add('is-error');
    return 'unavailable';
  }

  root.classList.remove('is-error');

  if (hasContributions && hasRepoStats && hasLanguages) {
    return 'full';
  }
  if (hasContributions && !hasRepoStats) {
    return 'partial-rate-limit';
  }
  return 'partial';
}

function statusMessage(kind) {
  switch (kind) {
    case 'full':
      return `Live from github.com/${GITHUB_USER}`;
    case 'partial-rate-limit':
      return `Contribution activity loaded. Repo and language stats refresh when GitHub's API rate limit resets.`;
    case 'partial':
      return `Partial stats from github.com/${GITHUB_USER}`;
    default:
      return 'GitHub stats unavailable right now. Check back later.';
  }
}

async function loadFreshStats() {
  let contributionsPayload = null;
  let profile = null;
  let repos = null;
  let languageTotals = {};

  try {
    contributionsPayload = await fetchJson(CONTRIBUTIONS_URL);
  } catch (error) {
    console.error(error);
  }

  try {
    [profile, repos] = await Promise.all([
      fetchJson(PROFILE_URL),
      fetchJson(REPOS_URL),
    ]);
  } catch (error) {
    console.error(error);
  }

  if (repos) {
    try {
      languageTotals = await loadLanguageTotals(repos);
    } catch (error) {
      console.error(error);
    }
  }

  const contributions = contributionsPayload?.contributions ?? [];
  const yearTotal = contributionsPayload?.total?.lastYear
    ?? contributions.reduce((sum, day) => sum + (day.count ?? 0), 0);
  const repoCount = profile?.public_repos ?? (repos ? repos.length : null);

  return {
    contributions,
    yearTotal,
    repoCount,
    languageTotals,
  };
}

export async function initGithubStats(root) {
  if (!(root instanceof HTMLElement)) return;

  const elements = {
    contribEl: root.querySelector('[data-gh="contributions"]'),
    reposEl: root.querySelector('[data-gh="repos"]'),
    languagesEl: root.querySelector('[data-gh="languages"]'),
    heatmapEl: root.querySelector('[data-gh="heatmap"]'),
    langBarsEl: root.querySelector('[data-gh="lang-bars"]'),
    statusEl: root.querySelector('[data-gh="status"]'),
  };

  const cached = readCache();
  if (cached) {
    const kind = applyStats(root, cached, elements);
    if (elements.statusEl) {
      elements.statusEl.textContent = statusMessage(kind);
    }
    return;
  }

  const data = await loadFreshStats();
  const kind = applyStats(root, data, elements);

  if (kind !== 'unavailable') {
    writeCache(data);
  }

  if (elements.statusEl) {
    elements.statusEl.textContent = statusMessage(kind);
  }
}
